import { useState } from 'react';
import { isBefore } from 'date-fns';

export enum SorteringType {
  DATE_TIME = 'date-time',
  EENHEID_NUMMER = 'eenheid-nummer',
  TREIN_NUMMER = 'trein-nummer',
  SIGNAAL_TYPE = 'signaal-type',
  BEGIN_TIJD = 'begintijd',
  DEFECT_SOORT = 'defect-soort',
  NIET_MEER_INZETBAAR_TIJD = 'niet-inzetbaar',
  CONFLICT_SOORT = 'conflict-soort',
  DIENST = 'oorspronkelijke-dienst',
  VERSTORINGSPUNTEN = 'verstoringspunten',
}

export enum SorteringTypeLabel {
  DATE_TIME = 'Datum/tijd',
  EENHEID_NUMMER = 'Eenheid',
  TREIN_NUMMER = 'Trein',
  SIGNAAL_TYPE = 'Signaaltype',
  BEGIN_TIJD = 'Begintijd defect',
  DEFECT_SOORT = 'Inzetbaarheid',
  NIET_MEER_INZETBAAR_TIJD = 'Begintijd niet inzetbaar',
  CONFLICT_SOORT = 'Conflicttype',
  DIENST = 'Omloop',
  VERSTORINGSPUNTEN = 'Verstoringspunten',
}

export enum SorteringRichting {
  UP = 'up',
  DOWN = 'down',
}

type SortingSelection = {
  sorteringType?: SorteringType;
  sorteringRichting?: SorteringRichting;
};

type SortingDefaults = Required<SortingSelection>;

export const resolveSorting = (selection: SortingSelection, defaults: SortingDefaults): SortingDefaults => ({
  sorteringType: selection.sorteringType ?? defaults.sorteringType,
  sorteringRichting: selection.sorteringRichting ?? defaults.sorteringRichting,
});

export const getSortSignalenOptions = () => {
  return [
    { label: SorteringTypeLabel.DATE_TIME, value: SorteringType.DATE_TIME },
    { label: SorteringTypeLabel.EENHEID_NUMMER, value: SorteringType.EENHEID_NUMMER },
    { label: SorteringTypeLabel.SIGNAAL_TYPE, value: SorteringType.SIGNAAL_TYPE },
    { label: SorteringTypeLabel.TREIN_NUMMER, value: SorteringType.TREIN_NUMMER },
  ];
};

export const getSortDefectenOptions = () => {
  return [
    { label: SorteringTypeLabel.BEGIN_TIJD, value: SorteringType.BEGIN_TIJD },
    { label: SorteringTypeLabel.NIET_MEER_INZETBAAR_TIJD, value: SorteringType.NIET_MEER_INZETBAAR_TIJD },
    { label: SorteringTypeLabel.EENHEID_NUMMER, value: SorteringType.EENHEID_NUMMER },
    { label: SorteringTypeLabel.DEFECT_SOORT, value: SorteringType.DEFECT_SOORT },
  ];
};

export const getSortConflictenOptions = () => {
  return [
    { label: SorteringTypeLabel.DATE_TIME, value: SorteringType.DATE_TIME },
    { label: SorteringTypeLabel.CONFLICT_SOORT, value: SorteringType.CONFLICT_SOORT },
    { label: SorteringTypeLabel.EENHEID_NUMMER, value: SorteringType.EENHEID_NUMMER },
    { label: SorteringTypeLabel.DIENST, value: SorteringType.DIENST },
    { label: SorteringTypeLabel.TREIN_NUMMER, value: SorteringType.TREIN_NUMMER },
    { label: SorteringTypeLabel.VERSTORINGSPUNTEN, value: SorteringType.VERSTORINGSPUNTEN },
  ];
};

export const stringComparator = (
  stringA: string | null | undefined,
  stringB: string | null | undefined,
  sortDirection: SorteringRichting | undefined,
  fallbackComparator?: () => number,
) => {
  if (fallbackComparator && ((!stringA && !stringB) || stringA === stringB)) {
    return fallbackComparator();
  }
  if (!stringA && !stringB) return 0;
  if (!stringA) return 1;
  if (!stringB) return -1;

  const directionMultiplier = sortDirection === SorteringRichting.UP ? 1 : -1;
  const resultingOrder = stringA.localeCompare(stringB, undefined, { sensitivity: 'base' });
  return resultingOrder != 0 ? directionMultiplier * resultingOrder : resultingOrder;
};

export const nummerComparator = (
  nummerA: number | null | undefined,
  nummerB: number | null | undefined,
  sortDirection: SorteringRichting | undefined,
  fallbackComparator?: () => number,
) => {
  if (fallbackComparator && ((!nummerA && !nummerB) || nummerA === nummerB)) {
    return fallbackComparator();
  }
  if ((!nummerA && !nummerB) || nummerA === nummerB) return 0;
  if (!nummerA) return 1;
  if (!nummerB) return -1;

  const directionMultiplier = sortDirection === SorteringRichting.UP ? -1 : 1;
  return nummerA < nummerB ? directionMultiplier : -directionMultiplier;
};

export const tijdComparator = (
  datumA: Date | string | null | undefined,
  datumB: Date | string | null | undefined,
  sortDirection: SorteringRichting | undefined,
  fallbackComparator?: () => number,
) => {
  const aTijd = datumA ? new Date(datumA) : null;
  const bTijd = datumB ? new Date(datumB) : null;

  if (fallbackComparator && ((!aTijd && !bTijd) || aTijd?.getTime() === bTijd?.getTime())) {
    return fallbackComparator();
  }
  if ((!aTijd && !bTijd) || aTijd?.getTime() === bTijd?.getTime()) return 0;
  if (!aTijd) return 1;
  if (!bTijd) return -1;

  const directionMultiplier = sortDirection === SorteringRichting.UP ? -1 : 1;
  return isBefore(aTijd, bTijd) ? directionMultiplier : -directionMultiplier;
};

export const useSorting = (
  initialValueSortType = SorteringType.DATE_TIME,
  initialValueSortDirection = SorteringRichting.UP,
) => {
  const [selectedSortType, setSelectedSortType] = useState<SorteringType>(initialValueSortType);
  const [selectedSortDirection, setSelectedSortDirection] = useState<SorteringRichting>(initialValueSortDirection);

  const handleSortChange = (selectedType: SorteringType) => {
    setSelectedSortType(selectedType);
  };

  const handleSortDirectionChange = (selectedDirection: SorteringRichting) => {
    setSelectedSortDirection(selectedDirection);
  };

  return {
    selectedSortType,
    selectedSortDirection,
    handleSortChange,
    handleSortDirectionChange,
    setSelectedSortType,
    setSelectedSortDirection,
  };
};
